import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Wrench, AlertTriangle, CircleDot, PhoneCall, Car, MessageSquare, ChevronRight, ShieldAlert, ClipboardCheck } from "lucide-react";
import { DriverShell } from "@/components/shells";
import { Panel, Spinner } from "@/components/kit";
import { OpsStatusBanner } from "@/components/OpsStatusBanner";
import { DriverInstallPrompt } from "@/components/pwa";
import { useAsync } from "@/lib/hooks";
import { loadDriverContext, loadDriverBookings } from "@/lib/driverData";
import { api } from "@/lib/api";
import { formatReg, fmtDate, REQUEST_LABEL } from "@/lib/vda";

const ACTIONS = [
  { key: "book", label: "Service & repair", hint: "Service, MOT, warning lights or a fault.", icon: Wrench, to: "/driver/book" },
  { key: "tyres", label: "Tyres", hint: "Puncture, damage or a pressure warning.", icon: CircleDot, to: "/driver/tyres" },
  { key: "glass", label: "Windscreen & glass", hint: "Chip, crack or broken glass.", icon: ShieldAlert, to: "/driver/glass" },
  { key: "bodywork", label: "Accident & bodywork", hint: "Report damage or an incident.", icon: AlertTriangle, to: "/driver/accident" },
  { key: "hire", label: "Request a hire vehicle", hint: "Ask for a temporary replacement vehicle.", icon: Car, to: "/driver/request?type=hire_vehicle" },
  { key: "ask", label: "Get help", hint: "Any other question about your vehicle.", icon: MessageSquare, to: "/driver/request?type=other" },
  { key: "breakdown", label: "Breakdown or emergency", hint: "Call for assistance now.", icon: PhoneCall, to: "/driver/breakdown", emergency: true },
];

function daysUntil(d) {
  if (!d) return null;
  return Math.ceil((new Date(d).getTime() - Date.now()) / 86400000);
}
function daysLabel(n) {
  if (n === null) return "—";
  if (n < 0) return `${Math.abs(n)} days ago`;
  if (n === 0) return "Today";
  return `in ${n} days`;
}

export default function DriverHome() {
  const nav = useNavigate();
  const { data, loading } = useAsync(async () => {
    const ctx = await loadDriverContext();
    const bookings = await loadDriverBookings();
    let vcheck = null;
    try { vcheck = await api.driverVehicleCheckConfig(); } catch (_) { /* ignore */ }
    return { ctx, bookings, vcheck };
  }, []);

  if (loading) return <DriverShell><Spinner /></DriverShell>;

  const { ctx, bookings } = data;
  const vcheck = data.vcheck;
  const v = ctx.vehicle;
  // Round 2 #2: enforce mandatory post-OTP mileage capture (once per session).
  if (v?.id) {
    let captured = false;
    try { captured = sessionStorage.getItem(`relay_mileage_${v.id}`) === "1"; } catch (_) { /* ignore */ }
    if (!captured) { nav("/driver/mileage", { replace: true }); return null; }
  }
  const open = bookings.filter((b) => b.public_status !== "Completed");
  const active = open[0] || null;
  const motDays = daysUntil(v?.mot_due_date);
  const svcDays = daysUntil(v?.service_due_date);

  if (!ctx.allocation || !v) {
    return (
      <DriverShell>
        <Panel className="p-6">
          <AlertTriangle className="h-5 w-5 text-[hsl(var(--vda-amber))]" />
          <h2 className="mt-3 font-semibold text-lg">No active vehicle allocation</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            We could not find an active vehicle allocated to you. Please contact your fleet team so they can update your records.
          </p>
        </Panel>
      </DriverShell>
    );
  }

  return (
    <DriverShell>
      <p className="text-sm text-muted-foreground" data-testid="driver-hello">Good day, {ctx.driver.full_name?.split(" ")[0] || "Driver"}.</p>
      <h1 className="text-[28px] leading-none font-semibold tracking-tight text-foreground mt-1" data-testid="driver-greeting">Your vehicle today</h1>

      <div className="mt-5"><OpsStatusBanner /></div>

      {/* Vehicle record — registration-led identity, delimited by rules (no card) */}
      <section className="mt-6 border-t border-foreground pt-4 pb-1" data-testid="driver-vehicle-card" aria-label="Allocated vehicle">
        <div className="flex items-center justify-between">
          <span className="vda-tick text-[hsl(var(--vda-quiet))]">Allocated vehicle</span>
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[hsl(var(--vda-success))]">
            <span className="vda-marker" style={{ backgroundColor: "hsl(var(--vda-success))" }} aria-hidden="true" /> Active allocation
          </span>
        </div>
        <div className="vda-reg text-[35px] leading-none font-bold tracking-wide text-foreground mt-3 mb-1.5">{formatReg(v.registration)}</div>
        <div className="text-base font-semibold text-foreground">{v.make} {v.model}</div>
        <div className="text-xs text-muted-foreground mt-1">
          {ctx.customer?.name}{v.current_mileage ? ` · ${v.current_mileage.toLocaleString()} miles` : ""}
        </div>
        <Link to="/driver/vehicle" data-testid="view-vehicle" className="inline-block mt-4 text-xs font-bold text-primary hover:underline">View vehicle record →</Link>
      </section>

      {/* Maintenance ledger */}
      <section className="mt-5 border-t border-[hsl(var(--vda-strong-border))]" aria-label="Maintenance status">
        {[
          { label: "MOT", date: v.mot_due_date, days: motDays },
          { label: "SERVICE", date: v.service_due_date, days: svcDays },
        ].map((row) => {
          const urgent = row.days !== null && row.days <= 30;
          return (
            <div key={row.label} className="grid grid-cols-[64px_1fr_auto] gap-3 items-center min-h-[68px] border-b border-border" data-testid={`ledger-${row.label.toLowerCase()}`}>
              <div className="vda-tick text-[hsl(var(--vda-quiet))]">{row.label}</div>
              <div>
                <strong className="block text-sm font-semibold">Due {fmtDate(row.date)}</strong>
                <span className="block mt-0.5 text-xs text-muted-foreground">{daysLabel(row.days)}</span>
              </div>
              <div className={`inline-flex items-center gap-1.5 font-mono text-[10px] font-bold whitespace-nowrap ${urgent ? "text-[hsl(var(--vda-amber))]" : "text-[hsl(var(--vda-success))]"}`}>
                <span className="vda-marker" style={{ backgroundColor: urgent ? "hsl(var(--vda-amber))" : "hsl(var(--vda-success))" }} aria-hidden="true" />
                {urgent ? daysLabel(row.days).replace("in ", "") : "On track"}
              </div>
            </div>
          );
        })}
      </section>

      {/* Active request — green left accent */}
      {active && (
        <Link to={`/driver/booking/${active.id}`} data-testid={`booking-row-${active.human_reference}`}
          className="block mt-6 border border-[hsl(var(--vda-strong-border))] border-l-[3px] border-l-primary rounded-[6px] bg-card hover:border-primary transition-colors">
          <div className="flex items-start justify-between gap-3 p-3.5 border-b border-border">
            <div>
              <span className="vda-tick text-[hsl(var(--vda-quiet))]">Active request · {active.human_reference}</span>
              <h2 className="mt-1 text-base font-semibold">{REQUEST_LABEL[active.request_type] || active.request_type}</h2>
            </div>
            <span className="inline-flex items-center gap-1.5 font-mono text-[10px] font-bold text-[hsl(var(--vda-amber))] whitespace-nowrap">
              <span className="vda-marker" style={{ backgroundColor: "hsl(var(--vda-amber))" }} aria-hidden="true" />{active.public_status}
            </span>
          </div>
          <div className="p-3.5">
            <span className="vda-tick text-[hsl(var(--vda-quiet))]">Current status</span>
            <p className="mt-1.5 text-xs text-muted-foreground leading-relaxed">
              {active.appointment_at ? `Appointment scheduled for ${fmtDate(active.appointment_at)}.` : "We are progressing your request. You do not need to call — updates appear here."}
            </p>
            <span className="mt-3 flex items-center justify-center w-full min-h-[43px] border border-[hsl(var(--vda-strong-border))] rounded-[6px] text-xs font-bold hover:border-primary hover:text-primary transition-colors">
              Track this request
            </span>
          </div>
        </Link>
      )}

      {/* Weekly vehicle check (config-driven, Sprint 7 Phase 3) */}
      {vcheck?.enabled && (
        <button data-testid="weekly-check-banner" onClick={() => nav("/driver/vehicle-check")}
          className={`w-full mt-4 grid grid-cols-[34px_1fr_18px] gap-3 items-center min-h-[64px] px-3 rounded-[8px] border text-left transition-colors ${vcheck.due ? "border-[hsl(var(--vda-amber))] bg-[hsl(var(--vda-amber))]/10 hover:bg-[hsl(var(--vda-amber))]/15" : "border-border hover:bg-secondary/40"}`}>
          <span className="grid place-items-center h-[34px] w-[34px] border border-border rounded-[6px] text-primary"><ClipboardCheck className="h-4 w-4" strokeWidth={1.8} /></span>
          <span>
            <strong className="block text-[13px] font-semibold">Weekly vehicle check {vcheck.due ? "· due now" : "· complete"}</strong>
            <small className="block mt-0.5 text-[10px] text-muted-foreground">{vcheck.last_check?.submitted_at ? `Last completed ${fmtDate(vcheck.last_check.submitted_at)}` : "Not completed yet"}</small>
          </span>
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </button>
      )}

      {/* Actions */}
      <h2 className="mt-7 mb-1 text-sm font-bold tracking-tight">What do you need?</h2>
      <div className="border-t border-[hsl(var(--vda-strong-border))]">
        {ACTIONS.filter((a) => !(a.key === "hire" && ctx?.customer?.hire_policy === "unavailable")).map((a) => (
          <button key={a.key} data-testid={`action-${a.key}`} onClick={() => nav(a.to)}
            className={`w-full grid grid-cols-[34px_1fr_18px] gap-3 items-center min-h-[64px] border-b border-border text-left transition-colors hover:text-primary ${a.emergency ? "text-[hsl(var(--vda-red))] hover:text-[hsl(var(--vda-red))]" : ""}`}>
            <span className={`grid place-items-center h-[34px] w-[34px] border rounded-[6px] ${a.emergency ? "border-[hsl(var(--vda-red))]/30 text-[hsl(var(--vda-red))]" : "border-border text-muted-foreground"}`}>
              <a.icon className="h-4 w-4" strokeWidth={1.8} />
            </span>
            <span>
              <strong className="block text-[13px] font-semibold">{a.label}</strong>
              <small className="block mt-0.5 text-[10px] text-muted-foreground">{a.hint}</small>
            </span>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </button>
        ))}
      </div>

      {bookings.length > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <h2 className="text-sm font-bold tracking-tight">Recent requests</h2>
          <Link to="/driver/history" data-testid="history-link" className="text-xs font-bold text-primary hover:underline">View all →</Link>
        </div>
      )}

      <DriverInstallPrompt />
    </DriverShell>
  );
}
